import { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router';
import { toast } from '@/components';
import { useApi } from '@/api/hooks';
import { useAuthStore } from '@/stores/auth';
import styles from './ExcelFormBuilder.module.css';

/* ── Types ── */

interface ExcelField {
  id: string;
  cell_ref: string;
  merge_range: string | null;
  sheet_name?: string;
  label: string;
  sub_label?: string;
  field_type: 'text' | 'number' | 'date' | 'select' | 'checkbox';
  db_mapping: string | null;
  required: boolean;
  order: number;
  // Extended fields for form builder
  visible_in_form?: boolean;
  input_method?: 'manual' | 'auto' | 'linked';
  default_value?: string | null;
  notes?: string | null;
}

interface MappingEntry {
  field_id: string;
  source_table: string;
  source_column: string;
  transform: string | null;
}

interface ExcelTemplate {
  id: string;
  name: string;
  description: string;
  category: string;
  file_name: string;
  file_size: number;
  sheet_name: string;
  row_count: number;
  col_count: number;
  fields: ExcelField[];
  mappings: MappingEntry[];
  merge_cells: unknown[];
  status: string;
  published_at: string | null;
  is_locked: boolean;
  current_version: number;
  created_at: string;
  updated_at: string;
}

/* ── Constants ── */

type ViewMode = 'excel' | 'form' | 'table';
type ZoomSize = 'XS' | 'S' | 'M' | 'L' | 'XL';

const VIEW_LABELS: Record<ViewMode, string> = { excel: 'Excel表示', form: 'フォーム表示', table: 'テーブル表示' };
const ZOOM_SCALES: Record<ZoomSize, number> = { XS: 0.5, S: 0.7, M: 1, L: 1.3, XL: 1.6 };

const FIELD_TYPES: Record<string, string> = { text: 'テキスト', number: '数値', date: '日付', select: '選択', checkbox: 'チェック' };

const INPUT_METHODS: Record<string, string> = {
  manual: '手動入力',
  auto: '自動入力',
  linked: 'マス連動',
};

const MAPPABLE_TABLES: Record<string, string> = {
  properties: '【物件】',
  customers: '【顧客】',
  contracts: '【契約】',
  tenant: '【テナント設定】',
};

/* ── Component ── */

export function ExcelFormBuilder() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  // Data
  const [template, setTemplate] = useState<ExcelTemplate | null>(null);
  const [fields, setFields] = useState<ExcelField[]>([]);
  const [mappings, setMappings] = useState<MappingEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const { data: mappableColumns } = useApi<Record<string, Record<string, string>>>('/console/excel-templates/mappable-columns');

  // UI state
  const [viewMode, setViewMode] = useState<ViewMode>('excel');
  const [zoom, setZoom] = useState<ZoomSize>('M');
  const [selectedFieldId, setSelectedFieldId] = useState<string | null>(null);
  const [panelOpen, setPanelOpen] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  // Excel Layout (miraiLMS方式)
  const [layout, setLayout] = useState<{
    sheetName: string; rows: number; cols: number;
    mergedCells: Array<{ start: string; end: string; startRow: number; startCol: number; endRow: number; endCol: number }>;
    cells: Array<{ cell: string; row: number; col: number; value: string; isInput: boolean; isYellow: boolean; style?: Record<string, unknown>; label?: string; fieldType?: string }>;
    colWidths: number[]; rowHeights: number[];
  } | null>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);

  /* ── Fetch template ── */
  useEffect(() => {
    if (!id) return;
    (async () => {
      try {
        const base = import.meta.env.VITE_API_URL || '/api';
        const { token } = useAuthStore.getState();
        const res = await fetch(`${base}/console/excel-templates/${id}`, {
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        });
        if (!res.ok) throw new Error('Failed to fetch');
        const data = await res.json() as ExcelTemplate;
        setTemplate(data);
        setFields((data.fields || []).map(f => ({
          ...f,
          visible_in_form: f.visible_in_form ?? true,
          input_method: f.input_method ?? (data.mappings?.find(m => m.field_id === f.id)?.source_table ? 'auto' : 'manual'),
          default_value: f.default_value ?? null,
          notes: f.notes ?? null,
        })));
        setMappings(data.mappings || []);
      } catch { toast('テンプレートの取得に失敗しました', 'error'); navigate('/excel-templates'); }
      finally { setLoading(false); }
    })();
  }, [id, navigate]);

  /* ── Fetch layout JSON ── */
  const loadLayout = useCallback(async () => {
    if (!id) return;
    try {
      const base = import.meta.env.VITE_API_URL || '/api';
      const { token } = useAuthStore.getState();
      const res = await fetch(`${base}/console/excel-templates/${id}/preview?format=layout`, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setLayout(await res.json());
    } catch { toast('レイアウトの取得に失敗しました', 'error'); }
  }, [id]);

  useEffect(() => { if (template) loadLayout(); }, [template, loadLayout]);

  /* ── Warn on unsaved changes ── */
  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => { if (dirty) e.preventDefault(); };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [dirty]);

  /* ── Field update helpers ── */
  const selectedField = fields.find(f => f.id === selectedFieldId) || null;

  const updateField = useCallback((fieldId: string, patch: Partial<ExcelField>) => {
    setFields(prev => prev.map(f => f.id === fieldId ? { ...f, ...patch } : f));
    setDirty(true);
  }, []);

  const updateMapping = useCallback((fieldId: string, table: string, column: string) => {
    setMappings(prev => {
      if (!table) return prev.filter(m => m.field_id !== fieldId);
      const exists = prev.find(m => m.field_id === fieldId);
      if (exists) return prev.map(m => m.field_id === fieldId ? { ...m, source_table: table, source_column: column } : m);
      return [...prev, { field_id: fieldId, source_table: table, source_column: column, transform: null }];
    });
    setDirty(true);
  }, []);

  /* ── Save ── */
  const handleSave = useCallback(async () => {
    if (!id) return;
    setSaving(true);
    try {
      const base = import.meta.env.VITE_API_URL || '/api';
      const { token } = useAuthStore.getState();
      const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

      // Save fields and mappings in parallel
      const [fRes, mRes] = await Promise.all([
        fetch(`${base}/console/excel-templates/${id}/fields`, {
          method: 'PATCH', headers, body: JSON.stringify({ fields }),
        }),
        fetch(`${base}/console/excel-templates/${id}/mappings`, {
          method: 'PATCH', headers, body: JSON.stringify({ mappings }),
        }),
      ]);

      if (!fRes.ok || !mRes.ok) { toast('保存に失敗しました', 'error'); return; }
      toast('保存しました');
      setDirty(false);
      loadLayout();
    } catch { toast('保存に失敗しました', 'error'); }
    finally { setSaving(false); }
  }, [id, fields, mappings, loadLayout]);

  /* ── Publish ── */
  const handlePublish = useCallback(async () => {
    if (!id || !confirm('テンプレートを公開しますか？\n公開するとテナント側で利用可能になります。')) return;
    try {
      const base = import.meta.env.VITE_API_URL || '/api';
      const { token } = useAuthStore.getState();
      const res = await fetch(`${base}/console/excel-templates/${id}/publish`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      if (!res.ok) { const d = await res.json().catch(() => ({})); toast(d.error || '公開に失敗しました', 'error'); return; }
      toast('テンプレートを公開しました');
      setTemplate(prev => prev ? { ...prev, status: 'active', published_at: new Date().toISOString() } : prev);
    } catch { toast('公開に失敗しました', 'error'); }
  }, [id]);

  /* ── Stats ── */
  const totalFields = fields.length;
  const mappedCount = fields.filter(f => mappings.some(m => m.field_id === f.id && m.source_table)).length;
  const unmappedCount = totalFields - mappedCount;

  /* ── Render helpers ── */

  const getMappingValue = (fieldId: string) => {
    const entry = mappings.find(mp => mp.field_id === fieldId);
    return entry ? `${entry.source_table}.${entry.source_column}` : '';
  };

  if (loading) return <div className={styles.loadingPage}>読み込み中...</div>;
  if (!template) return <div className={styles.loadingPage}>テンプレートが見つかりません</div>;

  return (
    <div className={styles.page}>
      {/* ── Header ── */}
      <div className={styles.header}>
        <button className={styles.backBtn} onClick={() => navigate('/excel-templates')}>
          ← 戻る
        </button>
        <span className={styles.headerTitle}>{template.name}</span>
        <span className={`${styles.headerBadge} ${template.status === 'active' ? styles.activeBadge : styles.draftBadge}`}>
          {template.status === 'active' ? '公開中' : '下書き'}
        </span>
        <div className={styles.headerActions}>
          <button className={styles.saveBtn} onClick={handleSave} disabled={saving || !dirty}>
            {saving ? '保存中...' : dirty ? '保存 *' : '保存'}
          </button>
          <button className={styles.publishBtn} onClick={handlePublish}>公開</button>
        </div>
      </div>

      {/* ── Body ── */}
      <div className={styles.body}>
        {/* ── Main Area ── */}
        <div className={styles.main}>
          {/* ── Toolbar ── */}
          <div className={styles.toolbar}>
            {(Object.keys(VIEW_LABELS) as ViewMode[]).map(mode => (
              <button key={mode}
                className={`${styles.tabBtn} ${viewMode === mode ? styles.tabBtnActive : ''}`}
                onClick={() => setViewMode(mode)}
              >{VIEW_LABELS[mode]}</button>
            ))}

            {viewMode === 'excel' && (
              <div className={styles.sizeGroup}>
                <span className={styles.sizeLabel}>表示サイズ:</span>
                {(Object.keys(ZOOM_SCALES) as ZoomSize[]).map(size => (
                  <button key={size}
                    className={`${styles.sizeBtn} ${zoom === size ? styles.sizeBtnActive : ''}`}
                    onClick={() => setZoom(size)}
                  >{size}</button>
                ))}
              </div>
            )}

            <button className={styles.expandBtn} onClick={() => setPanelOpen(p => !p)}>
              {panelOpen ? '▶ 拡大' : '◀ パネル'}
            </button>
          </div>

          {/* ── Content Area ── */}
          <div className={styles.previewArea}>
            {viewMode === 'excel' && (
              layout ? <ExcelTable layout={layout} fields={fields} mappings={mappings} selectedFieldId={selectedFieldId}
                zoom={ZOOM_SCALES[zoom]} onCellClick={(cellAddr) => {
                  const match = fields.find(f => {
                    const ref = f.cell_ref.includes('!') ? f.cell_ref.split('!')[1] : f.cell_ref;
                    if (ref === cellAddr) return true;
                    if (f.merge_range) {
                      const mr = f.merge_range.includes('!') ? f.merge_range.split('!')[1] : f.merge_range;
                      if (mr.split(':')[0] === cellAddr) return true;
                    }
                    return false;
                  });
                  if (match) { setSelectedFieldId(match.id); setPanelOpen(true); }
                }} />
              : <div className={styles.previewLoading}>レイアウトを読み込み中...</div>
            )}

            {viewMode === 'form' && (
              <div className={styles.formView}>
                <div className={styles.formGrid}>
                  {fields.map(field => {
                    const mapping = mappings.find(m => m.field_id === field.id);
                    const isMapped = !!(mapping?.source_table);
                    const isHidden = field.visible_in_form === false;
                    const isSelected = field.id === selectedFieldId;
                    return (
                      <div key={field.id}
                        className={`${styles.formCard} ${isHidden ? styles.formCardHidden : ''} ${isSelected ? styles.formCardSelected : ''}`}
                        onClick={() => { setSelectedFieldId(field.id); setPanelOpen(true); }}
                      >
                        <div className={styles.formCardLabel}>
                          <span className={styles.formCardCell}>{field.cell_ref}</span>
                          {field.required && <span className={styles.requiredMark}>*</span>}
                          {field.label || '(ラベルなし)'}
                        </div>
                        <input className={styles.formCardInput}
                          type={field.field_type === 'number' ? 'number' : field.field_type === 'date' ? 'date' : 'text'}
                          placeholder={field.default_value || (isMapped ? `自動: ${mapping!.source_table}.${mapping!.source_column}` : '入力してください')}
                          readOnly
                        />
                        <div className={styles.formCardMeta}>
                          <span className={`${styles.formCardBadge} ${isMapped ? styles.mappedBadge : styles.manualBadge}`}>
                            {isMapped ? '自動入力' : '手動入力'}
                          </span>
                          <span>{FIELD_TYPES[field.field_type]}</span>
                          {field.sub_label && <span>{field.sub_label}</span>}
                          {isHidden && <span>(非表示)</span>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {viewMode === 'table' && (
              <div className={styles.tableView}>
                <table className={styles.dataTable}>
                  <thead>
                    <tr>
                      <th>セル</th>
                      <th>項目名</th>
                      <th>データ型</th>
                      <th>入力方式</th>
                      <th>連動元</th>
                      <th>必須</th>
                      <th>表示</th>
                      <th>デフォルト値</th>
                      <th>備考</th>
                    </tr>
                  </thead>
                  <tbody>
                    {fields.map(field => {
                      const isSelected = field.id === selectedFieldId;
                      return (
                        <tr key={field.id}
                          className={isSelected ? styles.selectedRow : ''}
                          onClick={() => { setSelectedFieldId(field.id); setPanelOpen(true); }}
                        >
                          <td><span className={styles.cellRef}>{field.merge_range || field.cell_ref}</span></td>
                          <td>
                            <input value={field.label} onChange={e => updateField(field.id, { label: e.target.value })} />
                          </td>
                          <td>
                            <select value={field.field_type} onChange={e => updateField(field.id, { field_type: e.target.value as ExcelField['field_type'] })}>
                              {Object.entries(FIELD_TYPES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                            </select>
                          </td>
                          <td>
                            <select value={field.input_method || 'manual'} onChange={e => updateField(field.id, { input_method: e.target.value as ExcelField['input_method'] })}>
                              {Object.entries(INPUT_METHODS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                            </select>
                          </td>
                          <td>
                            {(field.input_method === 'auto') ? (
                              <select value={getMappingValue(field.id)} onChange={e => {
                                const dot = e.target.value.indexOf('.'); const t = dot > -1 ? e.target.value.slice(0, dot) : ''; const c = dot > -1 ? e.target.value.slice(dot + 1) : '';
                                updateMapping(field.id, t || '', c || '');
                              }}>
                                <option value="">--</option>
                                {mappableColumns && Object.entries(mappableColumns).map(([table, colMap]) =>
                                  Object.entries(colMap).map(([col, label]) => <option key={`${table}.${col}`} value={`${table}.${col}`}>{MAPPABLE_TABLES[table] || table} {label}</option>)
                                )}
                              </select>
                            ) : <span style={{ color: 'var(--ink3)' }}>--</span>}
                          </td>
                          <td className={styles.checkCenter}>
                            <input type="checkbox" checked={field.required} onChange={e => updateField(field.id, { required: e.target.checked })} />
                          </td>
                          <td className={styles.checkCenter}>
                            <input type="checkbox" checked={field.visible_in_form !== false} onChange={e => updateField(field.id, { visible_in_form: e.target.checked })} />
                          </td>
                          <td>
                            <input value={field.default_value || ''} onChange={e => updateField(field.id, { default_value: e.target.value || null })} placeholder="--" />
                          </td>
                          <td>
                            <input value={field.notes || ''} onChange={e => updateField(field.id, { notes: e.target.value || null })} placeholder="--" />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* ── Status Bar ── */}
          <div className={styles.statusBar}>
            <div className={styles.statusItem}>
              <span className={`${styles.statusDot} ${styles.statusDotAll}`} />
              フィールド: {totalFields}個
            </div>
            <div className={styles.statusItem}>
              <span className={`${styles.statusDot} ${styles.statusDotMapped}`} />
              マッピング済: {mappedCount}個
            </div>
            <div className={styles.statusItem}>
              <span className={`${styles.statusDot} ${styles.statusDotUnmapped}`} />
              未設定: {unmappedCount}個
            </div>
            {dirty && <span style={{ marginLeft: 'auto', color: 'var(--amber)', fontWeight: 600 }}>未保存の変更があります</span>}
          </div>
        </div>

        {/* ── Side Panel ── */}
        <div className={`${styles.sidePanel} ${!panelOpen ? styles.sidePanelCollapsed : ''}`}>
          {panelOpen && (
            <>
              <div className={styles.panelHeader}>
                <span className={styles.panelTitle}>フィールド設定</span>
                <button className={styles.panelClose} onClick={() => setPanelOpen(false)}>×</button>
              </div>
              <div className={styles.panelBody}>
                {!selectedField ? (
                  <div className={styles.panelEmpty}>
                    セルをクリックして<br />フィールドを選択してください
                  </div>
                ) : (
                  <>
                    {/* Cell reference */}
                    <div className={styles.panelCellRef}>
                      セル: {selectedField.merge_range || selectedField.cell_ref}
                    </div>

                    {/* Field name */}
                    <div className={styles.panelGroup}>
                      <div className={styles.panelLabel}>項目名</div>
                      <input className={styles.panelInput}
                        value={selectedField.label}
                        onChange={e => updateField(selectedField.id, { label: e.target.value })}
                        placeholder="フィールド名を入力"
                      />
                      {selectedField.sub_label && (
                        <div className={styles.panelHint}>サブラベル: {selectedField.sub_label}</div>
                      )}
                    </div>

                    {/* Visible in form */}
                    <div className={styles.panelGroup}>
                      <label className={styles.panelCheckbox}>
                        <input type="checkbox"
                          checked={selectedField.visible_in_form !== false}
                          onChange={e => updateField(selectedField.id, { visible_in_form: e.target.checked })}
                        />
                        フォームに表示する
                      </label>
                    </div>

                    <div className={styles.panelDivider} />

                    {/* Data type */}
                    <div className={styles.panelGroup}>
                      <div className={styles.panelLabel}>データ型</div>
                      <select className={styles.panelSelect}
                        value={selectedField.field_type}
                        onChange={e => updateField(selectedField.id, { field_type: e.target.value as ExcelField['field_type'] })}
                      >
                        {Object.entries(FIELD_TYPES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                      </select>
                    </div>

                    {/* Input method */}
                    <div className={styles.panelGroup}>
                      <div className={styles.panelLabel}>入力方式</div>
                      <select className={styles.panelSelect}
                        value={selectedField.input_method || 'manual'}
                        onChange={e => {
                          const method = e.target.value as ExcelField['input_method'];
                          updateField(selectedField.id, { input_method: method });
                          // Clear mapping if switching to manual
                          if (method === 'manual') {
                            updateMapping(selectedField.id, '', '');
                          }
                        }}
                      >
                        {Object.entries(INPUT_METHODS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                      </select>
                    </div>

                    {/* Mapping source (only for auto) */}
                    {selectedField.input_method === 'auto' && (
                      <div className={styles.panelGroup}>
                        <div className={styles.panelLabel}>連動元</div>
                        <select className={styles.panelSelect}
                          value={getMappingValue(selectedField.id)}
                          onChange={e => {
                            const dot = e.target.value.indexOf('.'); const t = dot > -1 ? e.target.value.slice(0, dot) : ''; const c = dot > -1 ? e.target.value.slice(dot + 1) : '';
                            updateMapping(selectedField.id, t || '', c || '');
                          }}
                        >
                          <option value="">-- 選択してください --</option>
                          {mappableColumns && Object.entries(mappableColumns).map(([table, colMap]) =>
                            Object.entries(colMap).map(([col, label]) => (
                              <option key={`${table}.${col}`} value={`${table}.${col}`}>{MAPPABLE_TABLES[table] || table} {label}</option>
                            ))
                          )}
                        </select>
                      </div>
                    )}

                    <div className={styles.panelDivider} />

                    {/* Required */}
                    <div className={styles.panelGroup}>
                      <label className={styles.panelCheckbox}>
                        <input type="checkbox"
                          checked={selectedField.required}
                          onChange={e => updateField(selectedField.id, { required: e.target.checked })}
                        />
                        必須項目
                      </label>
                    </div>

                    {/* Default value */}
                    <div className={styles.panelGroup}>
                      <div className={styles.panelLabel}>デフォルト値</div>
                      <input className={styles.panelInput}
                        value={selectedField.default_value || ''}
                        onChange={e => updateField(selectedField.id, { default_value: e.target.value || null })}
                        placeholder="初期値を入力"
                      />
                    </div>

                    {/* Notes */}
                    <div className={styles.panelGroup}>
                      <div className={styles.panelLabel}>備考</div>
                      <textarea className={styles.panelTextarea}
                        value={selectedField.notes || ''}
                        onChange={e => updateField(selectedField.id, { notes: e.target.value || null })}
                        placeholder="管理用メモ"
                      />
                    </div>

                    <div className={styles.panelDivider} />

                    <dl className={styles.panelExamples}>
                      <dt>入力方式の説明</dt>
                      <dd>手動入力: テナントが直接入力</dd>
                      <dd>自動入力: DBマッピングで自動入力</dd>
                      <dd>マス連動: 他フィールドから計算</dd>
                    </dl>
                  </>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/* ── ExcelTable: miraiLMS方式Reactテーブル描画 ── */

interface ExcelTableProps {
  layout: NonNullable<ReturnType<typeof useState<any>>[0]>;
  fields: ExcelField[];
  mappings: MappingEntry[];
  selectedFieldId: string | null;
  zoom: number;
  onCellClick: (cellAddr: string) => void;
}

function ExcelTable({ layout, fields, mappings, selectedFieldId, zoom, onCellClick }: ExcelTableProps) {
  // フィールドマップ (cell_ref → field)
  const fieldMap = useMemo(() => {
    const map = new Map<string, ExcelField>();
    for (const f of fields) {
      const ref = f.cell_ref.includes('!') ? f.cell_ref.split('!')[1] : f.cell_ref;
      map.set(ref, f);
      if (f.merge_range) {
        const mr = f.merge_range.includes('!') ? f.merge_range.split('!')[1] : f.merge_range;
        map.set(mr.split(':')[0], f);
      }
    }
    return map;
  }, [fields]);

  // セルデータマップ (row-col → cell)
  const cellMap = useMemo(() => {
    const map = new Map<string, typeof layout.cells[0]>();
    for (const c of layout.cells) map.set(`${c.row}-${c.col}`, c);
    return map;
  }, [layout.cells]);

  // 結合セル情報
  const { spans, skip } = useMemo(() => {
    const spans = new Map<string, { rowSpan: number; colSpan: number }>();
    const skip = new Set<string>();
    for (const m of layout.mergedCells) {
      spans.set(`${m.startRow}-${m.startCol}`, { rowSpan: m.endRow - m.startRow + 1, colSpan: m.endCol - m.startCol + 1 });
      for (let r = m.startRow; r <= m.endRow; r++)
        for (let c = m.startCol; c <= m.endCol; c++)
          if (r !== m.startRow || c !== m.startCol) skip.add(`${r}-${c}`);
    }
    return { spans, skip };
  }, [layout.mergedCells]);

  // 全行を列挙（hidden行はrowHeight=0で除外）
  const usedRows = useMemo(() => {
    const rows: number[] = [];
    for (let r = 1; r <= layout.rows; r++) {
      if ((layout.rowHeights[r - 1] || 0) > 0) rows.push(r);
    }
    return rows;
  }, [layout.rows, layout.rowHeights]);

  // 選択中フィールドのセル
  const selectedCells = useMemo(() => {
    if (!selectedFieldId) return new Set<string>();
    const f = fields.find(f => f.id === selectedFieldId);
    if (!f) return new Set<string>();
    const s = new Set<string>();
    const ref = f.cell_ref.includes('!') ? f.cell_ref.split('!')[1] : f.cell_ref;
    s.add(ref);
    if (f.merge_range) {
      const mr = f.merge_range.includes('!') ? f.merge_range.split('!')[1] : f.merge_range;
      s.add(mr.split(':')[0]);
    }
    return s;
  }, [selectedFieldId, fields]);

  // はみ出しテキストがある行（この行の入力セル背景を透明にする）
  const overflowRows = useMemo(() => {
    const rows = new Set<number>();
    for (const c of layout.cells) {
      if (!c.isInput && c.value && c.value.length > 30) {
        const nextKey = `${c.row}-${c.col + 1}`;
        const next = cellMap.get(nextKey);
        if (!next || !next.value) rows.add(c.row);
      }
    }
    return rows;
  }, [layout.cells, cellMap]);

  // テーブル合計幅
  const tableWidth = useMemo(() => {
    return layout.colWidths.reduce((sum: number, w: number) => sum + w, 0);
  }, [layout.colWidths]);

  // コンテナ幅を取得して自動フィットスケール算出
  const containerRef = useRef<HTMLDivElement>(null);
  const [fitScale, setFitScale] = useState(1);
  useEffect(() => {
    if (!containerRef.current || !tableWidth) return;
    const obs = new ResizeObserver(entries => {
      const w = entries[0]?.contentRect.width || 0;
      if (w > 0 && tableWidth > 0) setFitScale(Math.min((w - 16) / tableWidth, 1));
    });
    obs.observe(containerRef.current);
    return () => obs.disconnect();
  }, [tableWidth]);

  const scale = fitScale * zoom;

  return (
    <div ref={containerRef} style={{ fontSize: `${0.7 * scale}rem`, lineHeight: 1.3, overflow: 'auto', height: '100%', padding: '8px' }}>
      <div style={{ display: 'flex', gap: 16, marginBottom: 8, fontSize: '11px', color: 'var(--ink3)' }}>
        <span><span style={{ display: 'inline-block', width: 14, height: 14, background: '#ffffcc', border: '1px solid var(--border)', borderRadius: 2, verticalAlign: 'middle', marginRight: 4 }} />入力フィールド</span>
        <span><span style={{ display: 'inline-block', width: 14, height: 14, background: '#c8f7c5', border: '1px solid var(--border)', borderRadius: 2, verticalAlign: 'middle', marginRight: 4 }} />自動入力</span>
      </div>
      <table style={{ borderCollapse: 'collapse', tableLayout: 'fixed', width: tableWidth * scale }}>
        <colgroup>
          {layout.colWidths.map((w: number, i: number) => (
            <col key={i} style={{ width: w * scale }} />
          ))}
        </colgroup>
        <tbody>
          {usedRows.map(rowIdx => {
            const rh = layout.rowHeights[rowIdx - 1] || 20;
            return (
              <tr key={rowIdx} style={{ height: rh * scale }}>
                {Array.from({ length: layout.cols }, (_, i) => i + 1).map(colIdx => {
                  const key = `${rowIdx}-${colIdx}`;
                  if (skip.has(key)) return null;
                  const span = spans.get(key);
                  const cellData = cellMap.get(key);
                  const isInput = cellData?.isInput || false;
                  const field = isInput ? fieldMap.get(cellData?.cell || '') : null;
                  const isSelected = cellData?.cell ? selectedCells.has(cellData.cell) : false;
                  const mapping = field ? mappings.find(m => m.field_id === field.id) : null;
                  const isAuto = field?.input_method === 'auto' || !!(mapping?.source_table);

                  // 背景色（Excelの黄色セルを忠実再現）
                  const val = cellData?.value || '';
                  const hasRealValue = !!val.trim(); // スペースのみは空扱い
                  const isCheckbox = /[þ☑☐\u25A1✓✔]/.test(val);
                  const isYellowCell = cellData?.isYellow || false;
                  const isField = isInput || isYellowCell;
                  const inOverflowRow = overflowRows.has(rowIdx);
                  let bg = 'transparent';
                  let inputBorder = '';
                  if (isField && inOverflowRow && !hasRealValue && !isCheckbox) bg = 'transparent';
                  else if (isField && field?.visible_in_form === false) bg = 'rgba(200,200,200,0.15)';
                  else if (isField && isCheckbox) { bg = 'rgba(255,255,204,0.5)'; inputBorder = '1px solid rgba(200,180,0,0.3)'; }
                  else if (isField && hasRealValue) bg = 'transparent';
                  else if (isField && isAuto) { bg = '#c8f7c5'; inputBorder = '1px solid rgba(100,180,100,0.5)'; }
                  else if (isField) { bg = '#ffffcc'; inputBorder = '1px solid rgba(200,180,0,0.4)'; }
                  else if (cellData?.style?.bgColor) bg = cellData.style.bgColor as string;
                  if (isSelected) bg = '#a0d2ff';

                  const st = cellData?.style || {};

                  // 結合セル判定
                  const isMerged = !!(span && (span.rowSpan > 1 || span.colSpan > 1));
                  const isTallMerge = span && span.rowSpan > 3 && (span.colSpan || 1) <= 2;
                  const hasWrap = st.wrap || isMerged;

                  // 長文テキストが隣の空セルにはみ出すか判定（Excel挙動再現）
                  const valLen = (cellData?.value || '').length;
                  const isLongText = valLen > 30 && !isMerged && !isField;
                  // 右隣セルが空かチェック
                  const nextColKey = `${rowIdx}-${colIdx + 1}`;
                  const nextCell = cellMap.get(nextColKey);
                  const canOverflow = isLongText && (!nextCell || !nextCell.value);

                  return (
                    <td key={colIdx}
                      rowSpan={span?.rowSpan} colSpan={span?.colSpan}
                      onClick={isField ? () => onCellClick(cellData!.cell) : undefined}
                      style={{
                        border: inputBorder || '0.5px solid var(--border)',
                        padding: `${1 * scale}px ${2 * scale}px`,
                        background: canOverflow ? 'var(--bg, #1a1d23)' : bg,
                        cursor: isField ? 'pointer' : 'default',
                        fontWeight: st.bold ? 700 : 400,
                        fontSize: st.fontSize ? `${(st.fontSize as number) * scale}pt` : undefined,
                        fontStyle: st.italic ? 'italic' : undefined,
                        color: st.color as string || undefined,
                        textAlign: isTallMerge ? 'center' : (st.align as 'left' | 'center' | 'right') || undefined,
                        verticalAlign: isTallMerge ? 'top' : (st.valign as string) || 'middle',
                        whiteSpace: hasWrap ? 'normal' : 'nowrap',
                        wordBreak: isTallMerge ? 'break-all' : undefined,
                        overflow: 'visible',
                        textOverflow: undefined,
                        lineHeight: isTallMerge ? 1.1 : 1.3,
                        position: canOverflow ? 'relative' : undefined,
                        zIndex: canOverflow ? 10 : undefined,
                        outline: isSelected ? '2px solid var(--brand)' : undefined,
                        textDecoration: field?.visible_in_form === false ? 'line-through' : undefined,
                        opacity: field?.visible_in_form === false ? 0.4 : 1,
                      }}
                      title={isInput ? `${cellData?.cell}: ${field?.label || cellData?.label || '入力フィールド'}` : cellData?.value || ''}
                    >
                      {(cellData?.value || '').replace(/þ/g, '☑').replace(/¨/g, '□') || (isInput ? '' : '')}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
